// Audio manager for playing and preloading clips

export class AudioManager {
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  private currentAudio: HTMLAudioElement | null = null;

  // Preload an audio clip
  async preload(url: string, id: string): Promise<void> {
    const audio = new Audio(url);
    audio.preload = 'auto';
    this.audioElements.set(id, audio);
    
    return new Promise((resolve, reject) => {
      audio.addEventListener('canplaythrough', () => resolve(), { once: true });
      audio.addEventListener('error', (e) => reject(e), { once: true });
    });
  }

  // Play an audio clip
  async play(id: string): Promise<void> {
    const audio = this.audioElements.get(id);
    if (!audio) {
      throw new Error(`Audio clip ${id} not found`);
    }

    // Stop any currently playing audio
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }

    this.currentAudio = audio;
    return audio.play();
  }

  // Stop current audio
  stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
  }

  // Check if audio is playing
  isPlaying(): boolean {
    return this.currentAudio !== null && !this.currentAudio.paused;
  }

  // Get audio element for a clip (to attach event listeners)
  getAudio(id: string): HTMLAudioElement | undefined {
    return this.audioElements.get(id);
  }

  // Clean up
  cleanup(): void {
    this.audioElements.forEach((audio) => {
      audio.pause();
      audio.src = '';
    });
    this.audioElements.clear();
    this.currentAudio = null;
  }
}

// Singleton instance
export const audioManager = new AudioManager();